<template>
  <div
    class="no-shrink"
    :class="{ active }"
  >
    <div class="q-gutter-y-sm">
      <div
        v-if="!hideSources"
        class="block"
      >
        <span
          v-if="label"
          class="bold q-mb-sm block"
        >
          {{ label }}
        </span>

        <div v-if="excludedSources.length">
          {{ $t("sources_expandable.sources_not_available") }}
        </div>

        <FiltersAutocomplete
          v-else
          v-model="handleSources"
          :aria-label="label || $t('global.sources')"
          data-testid="sources-filter"
          :disabled="disabled"
          :filter-type="sourceFilterType"
          :hide-advanced-filters="hideAdvancedFilters"
          keep-expanded
          :media="media"
          :show-source-tags="showSourceTags"
          :stream="stream"
          :use-advanced-filters-modal="!hideAdvancedFilters"
          @source-filters-open="$emit('source-filters-open', $event)"
        />
      </div>

      <div
        v-if="!hideExcludedSources"
        class="block"
      >
        <span class="bold q-mb-sm block">{{
          $t("sources_expandable.excluded_sources")
        }}</span>

        <div v-if="sources.length">
          {{ $t("sources_expandable.excluded_sources_not_available") }}
        </div>

        <FiltersAutocomplete
          v-else
          v-model="handleExcludedSources"
          :aria-label="$t('sources_expandable.excluded_sources')"
          data-testid="excluded-sources-filter"
          :disabled="disabled"
          :filter-type="sourceFilterType"
          :hide-advanced-filters="hideAdvancedFilters"
          :hide-selector="hideExcludedSourcesSelector"
          keep-expanded
          :media="media"
          :show-source-tags="showSourceTags"
          :stream="stream"
          :use-advanced-filters-modal="!hideAdvancedFilters"
          @source-filters-open="$emit('source-filters-open', $event)"
        />
      </div>

      <div
        v-if="!hideAuthors"
        class="block"
      >
        <span class="bold q-mb-sm block">{{
          $t("sources_expandable.authors")
        }}</span>

        <FiltersAutocomplete
          v-model="handleAuthors"
          :aria-label="$t('sources_expandable.authors')"
          data-testid="authors-filter"
          :disabled="disabled"
          filter-type="author"
          :stream="stream"
        />
      </div>

      <div v-if="showTiers">
        <label class="bold q-py-sm block">
          {{ $t("sources_expandable.tiers") }}
        </label>
        <TierFilters
          v-model="handleTiers"
          :disabled="disabled"
          :stream="stream"
        />
      </div>

      <div v-if="showCategories">
        <label class="bold q-py-sm block">
          {{ $t("sources_expandable.categories") }}
        </label>
        <QCheckbox
          v-model="handleCategories"
          class="bold text-uppercase color-primary"
          color="white"
          :disabled="disabled"
          :label="$t('sources_expandable.national')"
          val="National"
        />
        <QCheckbox
          v-model="handleCategories"
          class="bold text-uppercase color-primary"
          color="white"
          :disabled="disabled"
          :label="$t('sources_expandable.metro')"
          val="Metro"
        />
        <QCheckbox
          v-model="handleCategories"
          class="bold text-uppercase color-primary"
          color="white"
          :disabled="disabled"
          :label="$t('sources_expandable.regional')"
          val="Regional"
        />
      </div>
    </div>
  </div>
</template>

<script>
import FiltersAutocomplete from "shared/components/core/forms/FiltersAutocomplete.vue";
import TierFilters from "shared/components/core/forms/TierFilters.vue";

export default {
  name: "SourcesExpandable",
  components: {
    FiltersAutocomplete,
    TierFilters,
  },
  props: {
    disabled: {
      type: Boolean,
    },
    stream: {
      type: Object,
      required: true,
    },
    media: {
      type: Array,
      default: () => [],
    },
    sources: {
      type: Array,
      default: () => [],
    },
    excludedSources: {
      type: Array,
      default: () => [],
    },
    authors: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    tiers: {
      type: Array,
      default: () => [],
    },
    hideAuthors: {
      type: Boolean,
    },
    showCategories: {
      type: Boolean,
    },
    showTiers: {
      type: Boolean,
    },
    hideSources: {
      type: Boolean,
    },
    hideExcludedSources: {
      type: Boolean,
    },
    hideExcludedSourcesSelector: {
      type: Boolean,
    },
    active: {
      type: Boolean,
    },
    label: {
      type: String,
      default: "",
    },
    searchAllSources: {
      type: Boolean,
    },
    hideAdvancedFilters: {
      type: Boolean,
    },
    showSourceTags: {
      type: Boolean,
    },
  },
  emits: [
    "update:sources",
    "search",
    "update:excludedSources",
    "update:authors",
    "update:categories",
    "update:tiers",
    "source-filters-open",
  ],
  computed: {
    handleSources: {
      get() {
        return this.sources;
      },
      set(val) {
        this.$track("Include sources in Mention Stream Filter");
        this.$emit("update:sources", val);
        this.$emit("search");
      },
    },
    handleExcludedSources: {
      get() {
        return this.excludedSources;
      },
      set(val) {
        this.$track("Exclude sources in Mention Stream Filter");
        this.$emit("update:excludedSources", val);
        this.$emit("search");
      },
    },
    handleAuthors: {
      get() {
        return this.authors;
      },
      set(val) {
        this.$emit("update:authors", val);
        this.$emit("search");
      },
    },
    handleCategories: {
      get() {
        return this.categories;
      },
      set(val) {
        this.$emit("update:categories", val);
        this.$emit("search");
      },
    },
    handleTiers: {
      get() {
        return this.tiers;
      },
      set(val) {
        this.$emit("update:tiers", val);
        this.$emit("search");
      },
    },
    sourceFilterType() {
      return this.searchAllSources ? "all_sources" : "sources";
    },
  },
};
</script>

<style lang="scss" scoped>
.label-extra {
  font-weight: normal;
  color: $ds-denim-7;
}

.text-input {
  border: 1px solid $silver-white;
  width: 100%;
}

.active {
  background-color: #fff;
}

:deep(.q-checkbox__bg) {
  height: 20px;
  width: 20px;
  min-height: 20px;
  min-width: 20px;
  background-color: #ebebeb;
  border-radius: 4px;
  cursor: pointer;
  border: none;
}

:deep(.q-checkbox__svg) {
  padding: 4px;
}

:deep(.q-checkbox__inner) {
  font-size: 35px;
}

:deep(.q-checkbox__inner::before) {
  background: none !important;
}

:deep(.q-checkbox__label) {
  font-size: 14px;
}
</style>
